import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Checkbox from '../../../../../../common/widgets/Checkbox';
import Icons from '../../../../../../common/widgets/Icons';
import Input from '../../../../../../common/widgets/Input';
import Loader from '../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../common/widgets/LoadingSpinner';
import { config } from '../../../../config';
import { resources } from '../../../../resources/resources';
import { getSelectedFilterIds } from '../helpers';
import { FilterItem } from '../types';

interface CheckboxFilterProps {
  title: string;
  filterItems: FilterItem[] | undefined;
  handleOnClick: (ids: number[]) => void;
  handleSearch?: (searchValue: string) => void;
}

export const CheckboxFilter = (props: Readonly<CheckboxFilterProps>) => {
  const { t: translate } = useTranslation();
  const { title, filterItems, handleOnClick, handleSearch } = props;
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [visibleFilterItems, setVisibleFilterItems] = useState<FilterItem[]>(
    filterItems?.slice(
      0,
      Math.min(filterItems.length, config.filtersToDisplay),
    ) ?? [],
  );

  const handleClick = (item: FilterItem) => {
    item.isChecked = !item.isChecked;
    const bIds = getSelectedFilterIds(visibleFilterItems ?? []);
    handleOnClick(bIds);
  };

  useEffect(() => {
    setVisibleFilterItems(
      filterItems?.slice(
        0,
        Math.min(filterItems.length, config.filtersToDisplay),
      ) ?? [],
    );
  }, [filterItems]);

  return (
    <Loader loading={!filterItems} loader={<LoadingSpinner />}>
      {filterItems && (
        <div
          className={`action-panel expandable expand-on-arrow ${
            isExpanded ? '' : 'collapsed'
          }`}
        >
          <div className="action-panel-title">
            <span>{title}</span>
            <span
              className="action-panel-expand"
              onClick={() => setIsExpanded(!isExpanded)}
              role="button"
            >
              <Icons.ArrowUp />
            </span>
          </div>

          {isExpanded && (
            <div className="action-panel-content">
              {handleSearch && (
                <Input
                  className="filter-search"
                  onChange={e => handleSearch(e.value)}
                  placeholder={`${translate(resources.search)}`}
                />
              )}
              <div
                className="styled-scrollbar filter-checkbox-list"
                style={{
                  display: 'block',
                  maxHeight: '315px',
                }}
              >
                {visibleFilterItems.map(i => (
                  <div key={`${i.id}-${i.value}`} className="filter-checkbox">
                    <Checkbox
                      isChecked={i.isChecked}
                      label={i.value as string}
                      onChange={() => handleClick(i)}
                      onLabelClick={() => handleClick(i)}
                    />
                  </div>
                ))}
              </div>
              {filterItems.length > config.filtersToDisplay &&
                visibleFilterItems.length <= config.filtersToDisplay && (
                  <div className="extra-filters">
                    <a
                      onClick={() => setVisibleFilterItems(filterItems)}
                    >{`${translate(resources.seeAll)} (${
                      filterItems.length
                    })`}</a>
                  </div>
                )}
            </div>
          )}
        </div>
      )}
    </Loader>
  );
};
